import { getAppState } from '../../app';
import { sedrApi, type SedrRoomSummary } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { iconImg } from '../../ui/icons';
import { showConfirmDialog } from '../../ui/confirmDialog';
import { showInputDialog } from '../../ui/inputDialog';
import { openFilesContextMenu } from '../../ui/filesContextMenu';
import { showSedrCreateDialog } from './sedrCreateDialog';
import { sedrErrorMessageKey } from './sedrErrors';
import { formatSedrDate, formatSedrDateTime, tSedrLifecycle } from './sedrUi';
import { createHelpTip } from '../../help/helpTips';
import { consumeCreateIntent, pushAppUrl } from '../../app/navigationState';
import { enhanceDataTableCards } from '../../ui/dataTableCards';

const DELETION_GRACE_DAYS = 7;

function isAdmin(room: SedrRoomSummary): boolean {
  return room.permission === 'Admin';
}

function currentUserId(): string {
  const state = getAppState();
  return state.kind === 'authenticated' ? state.session.userId : '';
}

function schedulePurgePreview(): string {
  return new Date(Date.now() + DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000).toLocaleString();
}

async function confirmRequestDeletion(room: SedrRoomSummary): Promise<string | null> {
  const confirmed = await showConfirmDialog({
    titleKey: 'sedr.deletion.requestTitle',
    messageKey: 'sedr.deletion.confirmRequestDetail',
    messageParams: {
      title: room.title,
      client: room.clientOrganization,
      files: room.fileCount,
      requirements: room.requirementCount,
      members: room.memberCount,
      guests: room.guestInviteCount,
      purgeAt: schedulePurgePreview(),
    },
    confirmLabelKey: 'sedr.deletion.request',
    confirmClassName: 'primary',
  });
  if (!confirmed) return null;
  return showInputDialog({
    titleKey: 'sedr.deletion.reasonTitle',
    labelKey: 'sedr.deletion.reasonPrompt',
    confirmLabelKey: 'sedr.deletion.request',
    multiline: true,
    required: true,
    maxLength: 2000,
  });
}

async function confirmApproveDeletion(room: SedrRoomSummary): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'sedr.deletion.approveTitle',
    messageKey: 'sedr.deletion.confirmApproveDetail',
    messageParams: {
      title: room.title,
      requester: room.deletionRequestedByName ?? room.deletionRequestedBy ?? '',
      reason: room.deletionReason?.trim() || '—',
      purgeAt: schedulePurgePreview(),
    },
    confirmLabelKey: 'sedr.deletion.approve',
    confirmClassName: 'primary',
  });
}

async function confirmImmediateDelete(room: SedrRoomSummary | { title: string }): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'sedr.deletion.deleteNowTitle',
    messageKey: 'sedr.deletion.confirmDeleteNow',
    messageParams: { title: room.title },
    confirmLabelKey: 'sedr.deletion.deleteNow',
    confirmClassName: 'primary',
  });
}

async function confirmArchive(room: SedrRoomSummary): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'sedr.archive.confirmTitle',
    messageKey: 'sedr.archive.confirmMessage',
    messageParams: { title: room.title },
    confirmLabelKey: 'sedr.archive.action',
    confirmClassName: 'primary',
  });
}

async function confirmUnarchive(room: SedrRoomSummary): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'sedr.archive.unarchiveTitle',
    messageKey: 'sedr.archive.unarchiveMessage',
    messageParams: { title: room.title },
    confirmLabelKey: 'sedr.archive.unarchiveAction',
    confirmClassName: 'primary',
  });
}

export type SedrListPageHandle = {
  element: HTMLElement;
  reload: () => Promise<void>;
};

export function SedrListPage(): SedrListPageHandle {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr';

  page.innerHTML = `
    <div class="module-section">
      <div class="module-section__header module-section__header--split">
        <div class="module-section__header-main">
          <h2 data-i18n="sedr.list.title"></h2>
          <p class="muted" data-i18n="sedr.list.subtitle"></p>
        </div>
        <div class="module-section__header-actions sedr-list-header-actions">
          <button type="button" class="button primary" id="sedr-create-room">
            <span class="button__icon"></span>
            <span data-i18n="sedr.list.create"></span>
          </button>
          <button
            type="button"
            class="icon-button sedr-list-header-actions__more"
            id="sedr-list-more"
            data-i18n-aria-label="sedr.list.moreActions"
            data-i18n-title="sedr.list.moreActions"
            aria-haspopup="menu"
          >⋯</button>
          <button type="button" class="button subtle sedr-list-header-actions__secondary" id="sedr-archived-toggle" data-i18n="sedr.archive.viewArchived"></button>
          <a href="/sedr/library" class="button subtle sedr-list-header-actions__secondary" id="sedr-library-link" data-i18n="sedr.library.manage"></a>
        </div>
      </div>
      <div id="sedr-room-list" class="files-table-wrap"></div>
      <p class="error-text" id="sedr-list-error" hidden></p>
    </div>
  `;

  page.querySelector('#sedr-create-room .button__icon')?.append(iconImg('sedr', 18));
  applyI18n(page);
  page.querySelector('.module-section__header-main h2')?.append(createHelpTip('sedr-setup'));

  const listEl = page.querySelector('#sedr-room-list') as HTMLElement;
  const errorEl = page.querySelector('#sedr-list-error') as HTMLParagraphElement;
  const archivedToggle = page.querySelector('#sedr-archived-toggle') as HTMLButtonElement;
  const createRoomBtn = page.querySelector('#sedr-create-room') as HTMLButtonElement;
  const libraryLink = page.querySelector('#sedr-library-link') as HTMLAnchorElement;
  const moreBtn = page.querySelector('#sedr-list-more') as HTMLButtonElement;
  let showingArchived = false;

  const openListMoreMenu = () => {
    openFilesContextMenu({
      anchorRect: moreBtn.getBoundingClientRect(),
      items: [
        {
          id: 'archived',
          labelKey: showingArchived ? 'sedr.archive.viewActive' : 'sedr.archive.viewArchived',
          action: () => {
            showingArchived = !showingArchived;
            void load();
          },
        },
        {
          id: 'library',
          labelKey: 'sedr.library.manage',
          hidden: showingArchived,
          action: () => pushAppUrl('/sedr/library'),
        },
      ],
    });
  };

  const showListError = (cause?: unknown) => {
    errorEl.textContent = t(cause !== undefined ? sedrErrorMessageKey(cause) : 'status.error');
    errorEl.hidden = false;
  };

  const load = async () => {
    errorEl.hidden = true;
    createRoomBtn.hidden = showingArchived;
    libraryLink.hidden = showingArchived;
    archivedToggle.setAttribute(
      'data-i18n',
      showingArchived ? 'sedr.archive.viewActive' : 'sedr.archive.viewArchived',
    );
    applyI18n(archivedToggle);
    try {
      const rooms = await sedrApi.listRooms(showingArchived);
      if (rooms.length === 0) {
        listEl.innerHTML = `<p class="muted" data-i18n="${showingArchived ? 'sedr.archive.empty' : 'sedr.list.empty'}"></p>`;
        applyI18n(listEl);
        return;
      }

      listEl.innerHTML = `
        <table class="data-table">
          <thead>
            <tr>
              <th data-i18n="sedr.list.colTitle"></th>
              <th data-i18n="sedr.list.colClient"></th>
              <th data-i18n="sedr.list.colStatus"></th>
              <th data-i18n="sedr.list.colLastModified"></th>
              <th class="data-table__actions-heading" data-i18n="sedr.list.colActions"></th>
            </tr>
          </thead>
          <tbody id="sedr-room-tbody"></tbody>
        </table>
      `;
      applyI18n(listEl);
      const tbody = listEl.querySelector('#sedr-room-tbody') as HTMLTableSectionElement;
      const me = currentUserId();

      for (const room of rooms) {
        const tr = document.createElement('tr');
        const link = document.createElement('a');
        link.href = `/sedr/${room.id}`;
        link.textContent = room.title;
        tr.innerHTML = `
          <td></td>
          <td></td>
          <td class="sedr-list-status"><div class="sedr-list-status__inner"><span class="status-badge"></span></div></td>
          <td class="sedr-list-modified"></td>
          <td class="data-table__actions"></td>
        `;
        tr.querySelector('td')?.append(link);
        const clientCell = tr.children[1] as HTMLTableCellElement;
        clientCell.textContent = room.clientOrganization;
        const statusInner = tr.querySelector('.sedr-list-status__inner') as HTMLElement;
        const badge = statusInner.querySelector('.status-badge') as HTMLElement;
        badge.textContent = room.archivedAt
          ? t('sedr.archive.status')
          : tSedrLifecycle(room.lifecycleState);
        if (room.archivedAt) {
          badge.classList.add('status-badge--muted');
        } else if (
          room.lifecycleState === 'PendingDeletion' ||
          room.lifecycleState === 'DeletionRequested'
        ) {
          badge.classList.add('status-badge--danger');
        }

        const unescape = { interpolation: { escapeValue: false } };
        if (!room.archivedAt && room.lifecycleState === 'DeletionRequested') {
          const info = document.createElement('span');
          info.className = 'muted';
          info.textContent = t('sedr.deletion.requestedBy', {
            name: room.deletionRequestedByName ?? room.deletionRequestedBy ?? '—',
            ...unescape,
          });
          statusInner.append(info);
        } else if (!room.archivedAt && room.lifecycleState === 'PendingDeletion') {
          const info = document.createElement('span');
          info.className = 'muted';
          info.textContent = room.purgeAt
            ? t('sedr.deletion.purgeOn', { date: formatSedrDate(room.purgeAt), ...unescape })
            : t('sedr.deletion.banner');
          statusInner.append(info);
        }

        const modifiedCell = tr.querySelector('.sedr-list-modified') as HTMLTableCellElement;
        const modified = document.createElement('span');
        modified.textContent = formatSedrDateTime(room.lastModifiedAt);
        const lastViewed = room.lastViewedAt ? new Date(room.lastViewedAt).getTime() : 0;
        const lastModified = room.lastModifiedAt ? new Date(room.lastModifiedAt).getTime() : 0;
        if (!room.lastViewedAt || lastModified > lastViewed) {
          modified.className = 'sedr-list-modified--unread';
          modified.title = t('sedr.list.unreadHint');
        }
        modifiedCell.append(modified);

        const actionsCell = tr.querySelector('.data-table__actions') as HTMLTableCellElement;
        if (isAdmin(room)) {
          const wrap = document.createElement('div');
          wrap.className = 'data-table__actions-inner';

          if (room.archivedAt) {
            const unarchiveBtn = document.createElement('button');
            unarchiveBtn.type = 'button';
            unarchiveBtn.className = 'button subtle';
            unarchiveBtn.textContent = t('sedr.archive.unarchiveAction');
            unarchiveBtn.addEventListener('click', async () => {
              if (!(await confirmUnarchive(room))) return;
              try {
                await sedrApi.unarchiveRoom(room.id);
                await load();
              } catch (cause) {
                showListError(cause);
              }
            });
            wrap.append(unarchiveBtn);
          } else if (room.lifecycleState === 'SetupIncomplete') {
            const deleteBtn = document.createElement('button');
            deleteBtn.type = 'button';
            deleteBtn.className = 'button subtle';
            deleteBtn.textContent = t('sedr.deletion.deleteNow');
            deleteBtn.addEventListener('click', async () => {
              if (!(await confirmImmediateDelete(room))) return;
              try {
                await sedrApi.deleteRoom(room.id);
                await load();
              } catch (cause) {
                showListError(cause);
              }
            });
            wrap.append(deleteBtn);
          } else if (room.lifecycleState === 'Active') {
            const archiveBtn = document.createElement('button');
            archiveBtn.type = 'button';
            archiveBtn.className = 'button subtle';
            archiveBtn.textContent = t('sedr.archive.action');
            archiveBtn.addEventListener('click', async () => {
              if (!(await confirmArchive(room))) return;
              try {
                await sedrApi.archiveRoom(room.id);
                await load();
              } catch (cause) {
                showListError(cause);
              }
            });
            wrap.append(archiveBtn);

            const requestBtn = document.createElement('button');
            requestBtn.type = 'button';
            requestBtn.className = 'button subtle';
            requestBtn.textContent = t('sedr.deletion.request');
            requestBtn.addEventListener('click', async () => {
              const reason = await confirmRequestDeletion(room);
              if (!reason) return;
              try {
                await sedrApi.scheduleDeletion(room.id, reason);
                await load();
              } catch (cause) {
                showListError(cause);
              }
            });
            wrap.append(requestBtn);
          } else if (room.lifecycleState === 'DeletionRequested') {
            if (room.deletionRequestedBy === me) {
              const withdrawBtn = document.createElement('button');
              withdrawBtn.type = 'button';
              withdrawBtn.className = 'button subtle';
              withdrawBtn.textContent = t('sedr.deletion.withdraw');
              withdrawBtn.addEventListener('click', async () => {
                try {
                  await sedrApi.cancelDeletion(room.id);
                  await load();
                } catch (cause) {
                  showListError(cause);
                }
              });
              wrap.append(withdrawBtn);
            } else {
              const approveBtn = document.createElement('button');
              approveBtn.type = 'button';
              approveBtn.className = 'button danger';
              approveBtn.textContent = t('sedr.deletion.approve');
              approveBtn.addEventListener('click', async () => {
                if (!(await confirmApproveDeletion(room))) return;
                try {
                  await sedrApi.decideDeletion(room.id, true);
                  await load();
                } catch (cause) {
                  showListError(cause);
                }
              });

              const rejectBtn = document.createElement('button');
              rejectBtn.type = 'button';
              rejectBtn.className = 'button subtle';
              rejectBtn.textContent = t('sedr.deletion.reject');
              rejectBtn.addEventListener('click', async () => {
                try {
                  await sedrApi.decideDeletion(room.id, false);
                  await load();
                } catch (cause) {
                  showListError(cause);
                }
              });
              wrap.append(approveBtn, rejectBtn);
            }
          } else if (room.lifecycleState === 'PendingDeletion') {
            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.className = 'button subtle';
            cancelBtn.textContent = t('sedr.deletion.cancel');
            cancelBtn.addEventListener('click', async () => {
              try {
                await sedrApi.cancelDeletion(room.id);
                await load();
              } catch (cause) {
                showListError(cause);
              }
            });
            wrap.append(cancelBtn);
          }

          if (wrap.childElementCount > 0) {
            actionsCell.append(wrap);
          }
        }

        tbody.append(tr);
      }

      const listTable = listEl.querySelector('table.data-table') as HTMLTableElement | null;
      if (listTable) {
        enhanceDataTableCards(listTable);
      }
    } catch {
      showListError();
    }
  };

  archivedToggle.addEventListener('click', () => {
    showingArchived = !showingArchived;
    void load();
  });

  moreBtn.addEventListener('click', openListMoreMenu);

  page.querySelector('#sedr-create-room')?.addEventListener('click', async () => {
    const created = await showSedrCreateDialog();
    if (!created) return;
    history.pushState({}, '', `/sedr/${created.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  if (consumeCreateIntent()) {
    void (async () => {
      const created = await showSedrCreateDialog();
      if (!created) return;
      history.pushState({}, '', `/sedr/${created.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    })();
  }

  void load();
  bindLocaleRefresh(load);
  return { element: page, reload: load };
}
